import '../App.css';
import {
    Box, Button,
    createTheme, CssBaseline, ThemeProvider,
    Stack, TextField, Typography, useMediaQuery,
    IconButton, CircularProgress,
} from "@mui/material";
import {ArrowBack, ImageOutlined, ColorLensOutlined} from "@mui/icons-material";
import {useEffect, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import SideBar from "../Componant/sidebar/Sidebar";
import QrColorFields, {DEFAULT_COLOR_FIELDS} from "../Componant/QrCode/QrColorFields";
import LogoField, {DEFAULT_LOGO_FIELD} from "../Componant/QrCode/LogoField";
import OptionSection from "../Componant/QrCode/OptionSection";
import SectionedMobileNav from "../Componant/QrCode/SectionedMobileNav";
import {getPalette, createPalette, updatePalette} from "../Utils/palettesApi";
import {generateQrCodePreview} from "../Utils/qrcodesApi";

const DEFAULT_PREVIEW_URL = 'https://alais-studio.fr';

function PaletteFormPage() {
    const {id} = useParams();
    const navigate = useNavigate();
    const isEditing = !!id;

    const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');
    const isComputer = useMediaQuery('(min-width:900px)');
    const [sidebarIsOpen, setSidebarIsOpen] = useState();

    const [name, setName] = useState('');
    const [style, setStyle] = useState({...DEFAULT_COLOR_FIELDS, ...DEFAULT_LOGO_FIELD});
    const [loading, setLoading] = useState(isEditing);
    const [saving, setSaving] = useState(false);

    const [previewUrl, setPreviewUrl] = useState(DEFAULT_PREVIEW_URL);
    const [previewImage, setPreviewImage] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewError, setPreviewError] = useState(null);

    const darkTheme = createTheme({
        palette: {
            mode: prefersDarkMode ? 'dark' : 'light',
            background: {default: prefersDarkMode ? '#1A1A1A' : '#f5f5f5'},
            primary: {main: '#FF7233', light: '#FF7233', dark: '#e05a1a'},
        },
        typography: {
            fontFamily: '"Poppins", sans-serif',
            allVariants: {color: prefersDarkMode ? '#cecece' : '#000000FF'},
        },
    });

    useEffect(() => {
        if (!id) return;
        getPalette(id)
            .then((palette) => {
                setName(palette.name);
                setStyle({...DEFAULT_COLOR_FIELDS, ...DEFAULT_LOGO_FIELD, ...palette.style});
            })
            .catch((error) => {
                console.error('Erreur lors du chargement de la palette:', error);
                alert('Palette introuvable');
                navigate('/palettes');
            })
            .finally(() => setLoading(false));
    }, [id, navigate]);

    // Aperçu QR live : régénéré (avec un léger débounce) à chaque changement de couleur ou d'URL de test.
    useEffect(() => {
        if (loading) return;

        setPreviewLoading(true);
        setPreviewError(null);
        const timer = setTimeout(() => {
            generateQrCodePreview({url: previewUrl || DEFAULT_PREVIEW_URL, ...style})
                .then((result) => {
                    if (!result?.image) throw new Error('Réponse invalide du serveur');
                    setPreviewImage(result.image);
                })
                .catch((error) => {
                    console.error('Erreur lors de la génération de l\'aperçu:', error);
                    setPreviewImage(null);
                    setPreviewError(error.response?.data?.error || error.message || 'Erreur inconnue');
                })
                .finally(() => setPreviewLoading(false));
        }, 400);

        return () => clearTimeout(timer);
    }, [loading, previewUrl, style]);

    const handleSubmit = async () => {
        if (!name.trim()) {
            alert('Le nom de la palette est requis');
            return;
        }

        setSaving(true);
        try {
            if (isEditing) {
                await updatePalette(id, {name, style});
            } else {
                await createPalette({name, style});
            }
            navigate('/palettes');
        } catch (error) {
            console.error('Erreur lors de l\'enregistrement de la palette:', error);
            alert('Erreur lors de l\'enregistrement de la palette');
        } finally {
            setSaving(false);
        }
    };

    const getContainerClass = () => {
        const classes = [];
        if (!isComputer) {
            classes.push("mobile");
        } else {
            classes.push(sidebarIsOpen ? 'activated' : 'disactivated');
        }
        return classes.join(' ');
    };

    const nameField = (
        <TextField
            label="Nom"
            fullWidth
            value={name}
            onChange={(e) => setName(e.target.value)}
        />
    );

    const previewBlock = (
        <Box width="100%">
            <Typography variant="h6" gutterBottom>Aperçu</Typography>
            <Box sx={{
                width: '100%', maxWidth: isComputer ? 'none' : 320, mx: isComputer ? 0 : 'auto',
                aspectRatio: '1 / 1',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                border: '1px solid', borderColor: 'divider', borderRadius: 2,
                position: 'relative', overflow: 'hidden', p: 2,
            }}>
                {previewImage && (
                    <Box component="img" src={previewImage} alt="Aperçu du QR code"
                         sx={{width: '100%', height: '100%', objectFit: 'contain'}}/>
                )}
                {!previewImage && !previewLoading && previewError && (
                    <Typography variant="body2" color="error" textAlign="center" sx={{px: 2}}>
                        {previewError}
                    </Typography>
                )}
                {!previewImage && !previewLoading && !previewError && (
                    <Typography variant="body2" color="text.secondary" textAlign="center" sx={{px: 2}}>
                        Aperçu indisponible
                    </Typography>
                )}
                {previewLoading && (
                    <Box sx={{
                        position: 'absolute', inset: 0, display: 'flex',
                        alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(0,0,0,0.15)',
                    }}>
                        <CircularProgress size={32}/>
                    </Box>
                )}
            </Box>

            <TextField
                label="URL de test"
                size="small"
                fullWidth
                sx={{mt: 2}}
                value={previewUrl}
                onChange={(e) => setPreviewUrl(e.target.value)}
            />
        </Box>
    );

    const updateStyle = (patch) => setStyle({...style, ...patch});

    return (
        <div className="App">
            <ThemeProvider theme={darkTheme}>
                <SideBar sidebarIsOpen={sidebarIsOpen} setSidebarIsOpen={setSidebarIsOpen} isComputer={isComputer}/>
                <CssBaseline/>

                <Box paddingTop={'5%'} paddingBottom={'5%'} id='Md-Container' className={getContainerClass()}>
                    <Box sx={{padding: {xs: 2, sm: 4}, margin: '0 auto', width: '100%', maxWidth: 1400}}>
                        <Stack direction="row" alignItems="center" spacing={2} mb={3}>
                            <IconButton onClick={() => navigate('/palettes')}><ArrowBack/></IconButton>
                            <Typography variant="h4">{isEditing ? 'Modifier la palette' : 'Nouvelle palette'}</Typography>
                        </Stack>

                        <Box sx={{px: {xs: 0, sm: 2}, pb: isComputer ? undefined : 9}}>
                            {loading ? (
                                <Typography>Chargement...</Typography>
                            ) : isComputer ? (
                                <>
                                    <Stack direction="row" spacing={5} alignItems="flex-start">
                                        <Box sx={{flex: '1 1 480px', maxWidth: 640, width: '100%'}}>
                                            {nameField}
                                        </Box>
                                        <Box sx={{flex: '0 0 auto', width: 380}}>
                                            {previewBlock}
                                        </Box>
                                    </Stack>

                                    <Stack spacing={2.5} sx={{mt: 5}}>
                                        <OptionSection title="Logo">
                                            <LogoField value={style} onChange={updateStyle}/>
                                        </OptionSection>
                                        <QrColorFields value={style} onChange={updateStyle}/>
                                    </Stack>
                                </>
                            ) : (
                                <>
                                    <Stack spacing={4}>
                                        {nameField}
                                        {previewBlock}
                                    </Stack>
                                    <SectionedMobileNav sections={[
                                        {key: 'logo', label: 'Logo', icon: <ImageOutlined/>, node: <LogoField value={style} onChange={updateStyle}/>},
                                        {key: 'colors', label: 'Couleurs', icon: <ColorLensOutlined/>, node: <QrColorFields value={style} onChange={updateStyle}/>},
                                    ]}/>
                                </>
                            )}

                            {!loading && (
                                <Stack direction="row" justifyContent="flex-end" spacing={2} sx={{mt: 5}}>
                                    <Button onClick={() => navigate('/palettes')}>Annuler</Button>
                                    <Button variant="contained" color="primary" onClick={handleSubmit} disabled={saving}>
                                        {saving ? 'Enregistrement...' : (isEditing ? 'Enregistrer' : 'Créer')}
                                    </Button>
                                </Stack>
                            )}
                        </Box>
                    </Box>
                </Box>
            </ThemeProvider>
        </div>
    );
}

export default PaletteFormPage;
